import React, { useEffect, useState } from "react";
import axios from "axios";
import {
    Container,
    Row,
    Col,
    Image,
    Form,
    Button,
    Card,
} from "react-bootstrap";
import { useFormik, Formik } from "formik";
import * as Yup from "yup";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useNavigate } from "react-router-dom";
import IconHeader from '../../images/logo-lg.png';
import ImageProfile from '../../images/empty-profile.png';
import IocnCamera from '../../images/icon-camera.png';
import QuoteSlider from "./QuoteSlider";
import { useSubscription } from '../../SubscriptionContext'; // Import useSubscription hook
import "./index.scss";
import apiConfig from "./apiConfig";

const quotes = [
    "Easily discover and quickly benefit from the most advanced technologies.",
    "Expert-level insights at your fingertips.",
    "Expedite planning, completion, and fieldwork procedures.",
    "Elevate your expertise and confidence.",
    "AI-powered audits: Save time, increase profitability, and improve quality.",
];

const validationSchema = Yup.object().shape({
    firstName: Yup.string()
    .trim()
    .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Only alphabets and single spaces allowed")
    .max(35, "First Name must be at most 35 characters")
    .required("First Name is required"),

  lastName: Yup.string()
    .trim()
    .matches(/^[A-Za-z]+(?: [A-Za-z]+)*$/, "Only alphabets and single spaces allowed")
    .max(35, "Last Name must be at most 35 characters")
    .required("Last Name is required"),
    email: Yup.string()
                .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email address")
                .required("Email is required"),
    phone: Yup.string()
    .transform(value => value.replace(/\D/g, "")) // Remove non-numeric characters before validation
    .matches(/^\d{10}$/, "Invalid phone number") // Ensure exactly 10 digits
    .required("Phone number is required"),
    companyName: Yup.string().required("Company Name is required"),
    title: Yup.string(),
    address1: Yup.string().required("Address 1 is required"),
    address2: Yup.string(),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    stateCode: Yup.string().required("State Code is required"),
    zipCode: Yup.string()
        .matches(/^[0-9]{5}$/, "Zip Code must be exactly 5 digits")
        .required("Zip Code is required"),
});

const Profile = () => {
    const navigate = useNavigate();
    const { setShowSubscriptionModal } = useSubscription(); // Use the subscription hook
    const [loading, setLoading] = useState(false);
    const [profileImage, setProfileImage] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [imageSrc, setImageSrc] = useState(null);
    const [states, setStates] = useState([]);
    const [statesLoading, setStatesLoading] = useState(false);
    const [statesError, setStatesError] = useState(null);
    const [buttonDisabled, setButtonDisabled] = useState(false);
    const [buttonTimeRemaining, setButtonTimeRemaining] = useState(0);
    const [initialFormValues, setInitialFormValues] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        companyName: "",
        title: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        stateCode: "",
        zipCode: "",
    });

    // Effect to handle the timer for the disabled button
    useEffect(() => {
        let timerId;

        if (buttonDisabled && buttonTimeRemaining > 0) {
            timerId = setTimeout(() => {
                setButtonTimeRemaining(prevTime => prevTime - 1);
            }, 1000);
        } else if (buttonTimeRemaining === 0 && buttonDisabled) {
            setButtonDisabled(false);
        }

        return () => clearTimeout(timerId);
    }, [buttonDisabled, buttonTimeRemaining]);

    //states API has been integrated here
    useEffect(() => {
        const fetchStates = async () => {
            setStatesLoading(true);
            setStatesError(null);
            try {
                const response = await axios.get(
                    `${apiConfig.apiBaseURL}/getAllStates`,
                    {
                        headers: {
                            "x-api-key": apiConfig["x-api-key"],
                            "Content-Type": "application/json",
                        },
                    }
                );
                if (response.data && response.data.success) {
                    const usStates = response.data.data.filter(
                        (state) => state.countryId === 231 && !state.deleted
                    );

                    usStates.sort((a, b) => a.name.localeCompare(b.name));

                    setStates(usStates);
                } else {
                    setStatesError("Failed to fetch states data");
                }
            } catch (error) {
                console.error("Error fetching states:", error);
                setStatesError(
                    "Error fetching states: " + (error.message || "Unknown error")
                );
            } finally {
                setStatesLoading(false);
            }
        };

        fetchStates();
    }, []);

    useEffect(() => {
        const userId = localStorage.getItem("userId");
        const fetchUserProfile = async () => {
            try {
                if (!userId) {
                    console.error("User ID not found. Please login again.");
                    return;
                }
                const response = await axios.get(
                    `${apiConfig.apiBaseURL}/getUserProfile/${userId}`,
                    {
                        headers: {
                            "x-api-key": apiConfig["x-api-key"],
                            "Content-Type": "application/json"
                        }
                    }
                );

                const data = response.data;
                if (data && data.success) {
                    setUserProfile(data.user);
                    setInitialFormValues({
                        firstName: data.user.firstName || "",
                        lastName: data.user.lastName || "",
                        email: data.user.email || "",
                        phone: data.user.phoneNumber || "",
                        companyName: data.user.companyName || "",
                        title: data.user.title || "",
                        address1: data.user.address1 || "",
                        address2: data.user.address2 || "",
                        city: data.user.city || "",
                        state: data.user.state || "",
                        stateCode: data.user.stateCode || "",
                        zipCode: data.user.zipCode || "",
                    });
                    toast.success("Profile data loaded successfully!", {
                        position: "top-right",
                        autoClose: 5000,
                    });
                } else {
                    setFetchError("Unable to load profile data");
                    toast.error("Failed to load profile data", {
                        position: "top-right",
                        autoClose: 5000,
                    });
                }
            } catch (error) {
                console.error("Error fetching user profile:", error);
                setFetchError(error.message || "Failed to fetch user profile");
                toast.error("Error loading profile: " + (error.message || "Unknown error"), {
                    position: "top-right",
                    autoClose: 5000,
                });
            }
        };
        fetchUserProfile();
    }, []);

    const handleSubmit = async (values, { setSubmitting }) => {
        setLoading(true);
        setButtonDisabled(true);
        setButtonTimeRemaining(15);

        const formdata = new FormData();
        formdata.append("id", localStorage.getItem("userId") || "");
        formdata.append("firstName", values.firstName || "");
        formdata.append("lastName", values.lastName || "");
        formdata.append("email", values.email || "");
        formdata.append("phoneNumber", values.phone || "");
        formdata.append("companyName", values.companyName || "");
        formdata.append("title", values.title || "");
        formdata.append("address1", values.address1 || "");
        formdata.append("address2", values.address2 || "");
        formdata.append("city", values.city || "");
        formdata.append("state", values.state || "");
        formdata.append("stateCode", values.stateCode || "");
        formdata.append("zipCode", values.zipCode || "");
        if (profileImage) {
            formdata.append('profilepicture', profileImage);
        }
        try {
            const response = await axios.post(
                `${apiConfig.apiBaseURL}/userProfileCreation`,
                formdata,
                {
                    headers: {
                        "x-api-key": apiConfig["x-api-key"],
                        "Content-Type": "multipart/form-data",
                    }
                }
            );

            if (response && response.data && response.data.success) {
                toast.success(response.data.message);
                
                // After successful profile update, check for subscription
                // If this is a new profile creation (determined by userProfile being null),
                // we should check subscription status
                if (!userProfile || !userProfile.stripeSubscriptionId) {
                    // Trigger the subscription modal after a delay
                    setTimeout(() => {
                        setShowSubscriptionModal(true);
                    }, 1000);
                } else {
                    // If user already has subscription, navigate to dashboard
                    setTimeout(() => {
                        navigate('/engagements');
                    }, 1000);
                }
            } else {
                const errorMessage = response?.data?.message || "Failed to update profile";
                toast.error(errorMessage);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || "Error updating profile";
            toast.error(errorMessage);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfileImage(file);
        }
    };

    useEffect(() => {
        if (profileImage instanceof Blob) {
            const objectUrl = URL.createObjectURL(profileImage);
            setImageSrc(objectUrl);
            return () => URL.revokeObjectURL(objectUrl); // Cleanup
        }
    }, [profileImage]);

    const getInitials = () => {
        const first = initialFormValues.firstName?.charAt(0) || '';
        const last = initialFormValues.lastName?.charAt(0) || '';
        return (first + last).toUpperCase();
    };
    

    return (
        <section className="credential-page create-profile">
            <Container fluid>
                <Row className="justify-content-md-center" style={{ minHeight: "100vh" }}>
                    {/* <Col lg={4} className="p-0 ">
                        <div className="left-side  d-md-flex align-items-center justify-content-center bg-light">
                            <QuoteSlider quotes={quotes} />
                        </div>
                    </Col> */}

                    <Col lg={8} md={10} className="right-side">
                        <div className="mb-4 card-title mt-5">
                            <h5 className="fw-bolder text-center" style={{fontSize:'40px'}}>
                                Profile
                            </h5>
                        </div>

                        <div className="profile-image-container text-center mb-3 position-relative mx-auto" style={{ width: '150px', height: '150px' }}>
                        {imageSrc || (userProfile && userProfile.profilePicture) ? (
    <Image
        src={imageSrc || userProfile.profilePicture}
        roundedCircle
        width={150}
        height={150}
        className="profile-image"
        onError={(e) => {
            e.target.onerror = null;
            setImageSrc(null);
        }}
    />
) : (
    <div
        className="d-flex align-items-center justify-content-center bg-secondary text-white rounded-circle profile-image"
        style={{
            width: "150px",
            height: "150px",
            fontSize: "3rem",
            border: '3px solid #FFFFFF'
        }}
    >
        {getInitials()}
    </div>
)}

                            <label className="edit-icon position-absolute bg-primary rounded-circle" style={{ bottom: -4, right: 0,  border: '3px solid #FFFFFF', cursor: 'pointer' }}>
                                <img src={IocnCamera} alt='click' style={{ padding: '10px' }} />
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={handleImageChange}
                                />
                            </label>
                        </div>
                        {fetchError && (
                            <div className="alert alert-danger" role="alert">
                                {fetchError}
                            </div>
                        )}

                        {statesError && (
                            <div className="alert alert-danger" role="alert">
                                {statesError}
                            </div>
                        )}

                        <Formik
                            initialValues={initialFormValues}
                            enableReinitialize={true}
                            validationSchema={validationSchema}
                            onSubmit={(values, { setSubmitting }) => {
                                handleSubmit(values, { setSubmitting });
                            }}
                        >
                            {(formik) => (
                                <Form onSubmit={formik.handleSubmit} noValidate>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>First Name*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter First Name"
                                                    {...formik.getFieldProps("firstName")}
                                                    isInvalid={formik.touched.firstName && formik.errors.firstName}
                                                    className="p-3"
                                                    maxLength={35}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.firstName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Last Name*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Last Name"
                                                    {...formik.getFieldProps("lastName")}
                                                    isInvalid={formik.touched.lastName && formik.errors.lastName}
                                                    className="p-3"
                                                    maxLength={35}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.lastName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Email*</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    placeholder="Enter Email Address"
                                                    {...formik.getFieldProps("email")}
                                                    isInvalid={formik.touched.email && formik.errors.email}
                                                    className="p-3"
                                                    disabled={userProfile !== null}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.email}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Phone*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Phone Number"
                                                    {...formik.getFieldProps("phone")}
                                                    onChange={(e) => {
                                                        let value = e.target.value.replace(/\D/g, "");
                                  
                                                        if (value.length > 10) {
                                                          value = value.slice(0, 10);
                                                        }
                                                        if (value.length > 6) {
                                                          value = `(${value.slice(0, 3)}) ${value.slice(
                                                            3,
                                                            6
                                                          )}-${value.slice(6, 10)}`;
                                                        } else if (value.length > 3) {
                                                          value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
                                                        } else if (value.length > 0) {
                                                          value = `(${value}`;
                                                        }
                                  
                                                        formik.setFieldValue("phone", value);
                                                      }}
                                                    isInvalid={formik.touched.phone && formik.errors.phone}
                                                    className="p-3"
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.phone}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Company Name*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Company Name"
                                                    {...formik.getFieldProps("companyName")}
                                                    isInvalid={formik.touched.companyName && formik.errors.companyName}
                                                    className="p-3"
                                                    maxLength={100}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.companyName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Title</Form.Label>
                                                <Form.Control type="text" placeholder="Enter Title" {...formik.getFieldProps("title")} className="p-3" maxLength={65}/>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Address 1*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Address 1"
                                                    {...formik.getFieldProps("address1")}
                                                    isInvalid={formik.touched.address1 && formik.errors.address1}
                                                    className="p-3"
                                                    maxLength={100}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.address1}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Address 2</Form.Label>
                                                <Form.Control type="text" placeholder="Enter Address 2" {...formik.getFieldProps("address2")} className="p-3" maxLength={100}/>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>City*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter City"
                                                    {...formik.getFieldProps("city")}
                                                    isInvalid={formik.touched.city && formik.errors.city}
                                                    className="p-3"
                                                    maxLength={50}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.city}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>State*</Form.Label>
                                                <Form.Select
                                                    name="state"
                                                    onChange={(e) => {
                                                        // Find the selected state object
                                                        const selectedState = states.find(
                                                            state => state.code === e.target.value
                                                        );
                                                        formik.setFieldValue("stateCode", e.target.value);
                                                        formik.setFieldValue("state", selectedState ? selectedState.name : "");
                                                    }}
                                                    value={formik.values.stateCode}
                                                    isInvalid={formik.touched.state && formik.errors.state}
                                                    disabled={statesLoading}
                                                    className="p-3"
                                                >
                                                    <option value="">Select State</option>
                                                    {statesLoading ? (
                                                        <option>Loading states...</option>
                                                    ) : (
                                                        states.map(state => (
                                                            <option key={state._id} value={state.code}>
                                                                {state.name}
                                                            </option>
                                                        ))
                                                    )}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.state}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Zip Code*</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter Zip Code"
                                                    {...formik.getFieldProps("zipCode")}
                                                    isInvalid={formik.touched.zipCode && formik.errors.zipCode}
                                                    className="p-3"
                                                    maxLength={5}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors.zipCode}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="my-4 border-0 text-white font-16 px-5 py-3 w-auto ms-auto"
                                            disabled={formik.isSubmitting || loading || buttonDisabled}
                                        >
                                            {loading ? "Updating..." : buttonDisabled ?
                                                `Please wait (${buttonTimeRemaining}s)` :
                                                (userProfile ? "Update Profile" : "Create Profile")}
                                        </Button>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </Col>
                </Row>
            </Container>
            {/* <ToastContainer /> */}
        </section>
    );
};

export default Profile;